/// <reference path="../../typings/browser.d.ts" />

namespace Application.Directives {
    'use strict';
    /**
     * Factory callback type
     */

    import ITsProbeFound = Models.ITsProbeFound;

    declare type ProbeDirectiveCb = (modalService: ngModal.IModalService,
                                     $timeout: ng.ITimeoutService,
                                     connectionRoomService: Services.ConnectionRoomService,
                                     tsProbeService: Services.TsProbeService,
                                     networkNodeTypeService: Services.NetworkNodeTypeService,
                                     networkNodeService: Services.NetworkNodeService,
                                     aclService: Services.AclService,
                                     $rootScope: Services.IFlexSettings) => ProbeDirective;

    /**
     * Custom scope
     */
    export interface IProbeDirectiveScopeDef {
        display: string;
        blockId: string;
        blockName: string;
        size: string;
        probeLink: string;
        isTsStream: string;
        audioMonitoringEnabled: string;
        audioLevelIsZero: string;
        startAudioProbing: string;
        stopAudioProbing: string;
        showAudio: string;
        probeOn: string;
        probeOff: string;
    }

    export interface IProbeDirectiveScope extends ng.IScope {
        display: string;
        tsProbes: Models.TsProbe[];
        hasAssignedProbe: boolean;
        assignedProbe: Models.ITsProbe;
        assignedProbesFound: Models.IFindByTsResults;
        assignedProbeLinkId: number;
        switching: boolean;
        blockId: number;
        blockName: string;
        size: string;
        probePermissions: Models.IEvaluatedAcl;
        isTsStream: boolean;
        audioMonitoringEnabled: boolean;
        audioLevelIsZero: () => boolean;
        startAudioProbing: (blockId: number) => void;
        stopAudioProbing: (blockId: number) => void;
        showAudio: boolean;

        lockingTSProbe: boolean;
        stopping: boolean;
        probeTooltipText: string;
        openTsProbeModal: () => void;
        removeTsProbeModal: () => void;
        switchDefaultProbe: (defaultProbe: ITsProbeFound) => void;
        probeStyle: string;
        probeLink: string;
        allAssignedProbes: ITsProbeFound[];
        hasLockedTSProbe: boolean;
        isLoading: boolean;

        modalOtherProbes: Models.ISelectableProbe[];
        modalSelectedProbes: Models.ISelectableProbe[];
        openingModal: boolean;
        probeSubscription: () => void;
        probeOn: () => string;
        probeOff: () => string;
    }

    export class ProbeDirective implements ng.IDirective {
        public static $name: string = "probe";

        public restrict: string = 'AE';
        public replace: boolean = true;
        public transclude: boolean = false;
        public templateUrl: string = 'decorators/probe-directive.html';
        public link: (scope: IProbeDirectiveScope, element: JQuery, attrs: ng.IAttributes) => void;
        public scope: IProbeDirectiveScopeDef = {
            display: '@',
            blockId: '=',
            blockName: '=',
            size: '@',
            probeLink: '=',
            isTsStream: '=',
            audioMonitoringEnabled: '=',
            audioLevelIsZero: '&',
            startAudioProbing: '&',
            stopAudioProbing: '&',
            showAudio: '=',
            probeOn: '&',
            probeOff: '&',
        };

        public static Factory(): ProbeDirectiveCb {
            let directive: ProbeDirectiveCb = (modalService: ngModal.IModalService,
                                               $timeout: ng.ITimeoutService,
                                               connectionRoomService: Services.ConnectionRoomService,
                                               tsProbeService: Services.TsProbeService,
                                               networkNodeTypeService: Services.NetworkNodeTypeService,
                                               networkNodeService: Services.NetworkNodeService,
                                               aclService: Services.AclService,
                                               $rootScope: Services.IFlexSettings) => {
                return new ProbeDirective(modalService, 
                                          $timeout, 
                                          connectionRoomService, 
                                          tsProbeService, 
                                          networkNodeTypeService, 
                                          networkNodeService,
                                          aclService,
                                          $rootScope);
            };
            directive.$inject = ['ModalService',
                '$timeout',
                'connectionRoomService',
                'tsProbeService',
                'networkNodeTypeService',
                'networkNodeService',
                'aclService',
                '$rootScope'];
            return directive;
        }

        constructor(private modalService: ngModal.IModalService,
                    private $timeout: ng.ITimeoutService,
                    private connectionRoomService: Services.ConnectionRoomService,
                    private tsProbeService: Services.TsProbeService,
                    private networkNodeTypeService: Services.NetworkNodeTypeService,
                    private networkNodeService: Services.NetworkNodeService,
                    private aclService: Services.AclService,
                    private $rootScope: Services.IFlexSettings) {
            ProbeDirective.prototype.link = ($scope: IProbeDirectiveScope, element: JQuery, attrs: ng.IAttributes) => {

                $scope.switching = false;

                $scope.$on('$destroy', () => {
                    if ($scope.probeSubscription) {
                        $scope.probeSubscription();
                    }
                    this.tsProbeService.stopTsProbeInterval($scope.blockId);
                });

                this.tsProbeService.getProbesCached().then((tsProbes: Models.TsProbe[]) => {
                    $scope.tsProbes = tsProbes;
                });

                if ($scope.blockId !== undefined) {
                    this.initProbeInfoSubscription($scope);
                    if ($scope.isTsStream) {
                        this.tsProbeService.initTsProbeInterval($scope.blockId);
                    }
                }

                $scope.$watch('blockId', (newValue: number, oldValue: number) => {
                    if (newValue !== undefined) {
                        if (newValue !== oldValue) {
                            this.tsProbeService.stopTsProbeInterval(oldValue);
                            this.initProbeInfoSubscription($scope);
                        }
                        if ($scope.isTsStream) {
                            this.tsProbeService.initTsProbeInterval(newValue);
                        }
                    }
                });

                $scope.$watch('isTsStream', (newValue: boolean, oldValue: boolean) => {
                    if (newValue !== oldValue || newValue) {
                        this.initProbeInfoSubscription($scope);
                    }
                });

                $scope.$watch( 'hasAssignedProbe && !lockingTSProbe && !switching && !stopping && !isLoading',
                    (newValue: boolean, oldValue: boolean) => {
                    let isP: boolean = $scope.hasAssignedProbe && !$scope.lockingTSProbe && !$scope.switching
                        && !$scope.stopping && !$scope.isLoading;
                    if (isP) {
                        if ($scope.probeOn) {
                            $scope.probeOn();
                        }
                    } else {
                        if ($scope.probeOff) {
                            $scope.probeOff();
                        }
                    }
                });

                $scope.openTsProbeModal = (): void => {
                    if ($scope.openingModal) {
                        return;
                    }
                    $scope.openingModal = true;
                    this.retrieveProbes($scope).then(() => {
                        const controller: any = $scope.tsProbes && $scope.tsProbes.length > 1
                            ? Controllers.TsDualListModalController : Controllers.TsProbeModalController;
                        const inputs: any = $scope.tsProbes && $scope.tsProbes.length > 1
                            ? {
                                title: `Select probing device(s) to be attached to ${$scope.blockName}`,
                                titleElements: 'Other devices',
                                titleSelectedElements: 'Selected devices',
                                allProbes: $scope.modalOtherProbes,
                                selectedProbes: $scope.modalSelectedProbes,
                            }
                            : {
                                probe: $scope.modalOtherProbes[0] ? $scope.modalOtherProbes[0] : $scope.modalSelectedProbes[0],
                                selectedProbe: $scope.assignedProbe
                            };
    
                        $scope.openingModal = false;
                        this.modalService.showModal({
                            templateUrl: controller.templateUrl,
                            controller: controller.$name,
                            inputs: inputs,
                        }).then((modal: ngModal.IModalObject) => {
                            let modalElem: UIkit.ModalElement = UIkit.modal(controller.ID,
                                {
                                    bgclose: false,
                                    modal: false,
                                    keyboard: false,
                                });
                            modalElem.show();
                            modal.close.then((result: Models.ISelectableProbe[]) => {
                                $scope.lockingTSProbe = true;
                                if (result !== undefined) {
                                    this.$timeout(() => {
                                        $scope.lockingTSProbe = false;
                                    }, 10000);
                                    const changes: Models.ITsChange[] = this.buildChanges(result);
                                    this.switchProbing($scope, changes, connectionRoomService, tsProbeService).then(() => {
                                        $scope.modalOtherProbes = [];
                                        $scope.modalSelectedProbes = [];
                                        modalElem.hide();
                                    });
                                } else {
                                    $scope.lockingTSProbe = false;
                                }
                            });
                        });
                    });
                };

                $scope.removeTsProbeModal = () => {
                    modalService
                        .showModal({
                            templateUrl: Controllers.ConfirmModalController.templateURL,
                            controller: Controllers.ConfirmModalController.$name,
                            inputs: {
                                title: 'Stop probing',
                                description: 'Stop probing this Transport Stream?',
                                name: '',
                                type: 'information',
                            },
                        })
                        .then((modal: ngModal.IModalObject) => {
                            let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                                {bgclose: false, modal: false, keyboard: false});
                            modalElem.show();
                            modal.close.then((result: Object) => {
                                modalElem.hide();
                                if (result) {
                                    tsProbeService.disconnectProbeByBlockId($scope.blockId).then((stopped: Models.ProbeOperationResult) => {
                                        if (stopped) {
                                            $scope.stopping = true;
                                            $scope.hasAssignedProbe = false;
                                            $scope.assignedProbe = undefined;
                                            $scope.allAssignedProbes = [];
                                            $scope.modalSelectedProbes = [];
                                        } else {
                                            this.showProbeErrors(stopped);
                                        }
                                    }).catch((reason: ngCustom.IReason) => {
                                        const result: Models.ProbeOperationResult =  {
                                            result: false,
                                            errors: ["Could not make connection with TNS"],
                                            message: ""
                                        };
                                        this.showProbeErrors(result);
                                    });
                                }
                            });
                        });
                };

                $scope.switchDefaultProbe = (defaultProbe: ITsProbeFound) => {
                    $scope.switching = true;
                    this.buildProbeLink($scope, defaultProbe);
                    $scope.allAssignedProbes.forEach((probeFound: ITsProbeFound) => {
                        probeFound.defaultProbe = false;
                    });
                    defaultProbe.defaultProbe = true;
                    tsProbeService.switchDefaultProbe($scope.blockId, defaultProbe.tsProbe.networkNodeId);
                };

            };
        }

        private initProbeInfoSubscription($scope: Application.Directives.IProbeDirectiveScope) {
            if ($scope.probeSubscription)
                $scope.probeSubscription(); //cancelling previous subscription
            $scope.isLoading = true;
            $scope.probeStyle = undefined;
            $scope.hasAssignedProbe = false;
            $scope.hasLockedTSProbe = false;
            $scope.probeTooltipText = "";
            $scope.assignedProbesFound = undefined;
            $scope.assignedProbe = undefined;
            $scope.probeLink = undefined;
            $scope.assignedProbeLinkId = undefined;
            $scope.allAssignedProbes = undefined;
            if ($scope.isTsStream) {
                $scope.probeSubscription = this.tsProbeService.subscribeConnectedProbeByBlockId($scope.blockId, ((findByTsResults: Models.IFindByTsResults) => {
                    $scope.isLoading = false;
                    if ($scope.switching) {
                        $scope.switching = false;
                    } else if (!$scope.openingModal) {
                        $scope.modalOtherProbes = [];
                        $scope.modalSelectedProbes = [];
                        $scope.assignedProbesFound = findByTsResults;
                        $scope.hasLockedTSProbe = $scope.assignedProbesFound && $scope.assignedProbesFound.selectedStream
                            && $scope.assignedProbesFound.defaultTsProbe && $scope.assignedProbesFound.defaultTsProbe.locked;
                        $scope.probeStyle = $scope.hasLockedTSProbe ? 'color:green' : 'color:red';
                        $scope.probeTooltipText = $scope.hasAssignedProbe ? $scope.assignedProbe.networkNodeName : "Probed signal";

                        if ($scope.assignedProbesFound &&
                            $scope.assignedProbesFound.defaultTsProbe) {
                            if ($scope.assignedProbesFound.defaultTsProbe.locked)
                                $scope.lockingTSProbe = false;
                            $scope.hasAssignedProbe = true;
                            const newAssignedProbe: Models.ITsProbe = $scope.assignedProbesFound.defaultTsProbe.tsProbe;
                            if (!$scope.assignedProbe || newAssignedProbe.networkNodeId !== $scope.assignedProbe.networkNodeId) {
                                //Load permissions
                                this.tsProbeService.getProbesCached().then((tsProbes: Models.TsProbe[]) => {
                                    for (const tsProbe of tsProbes) {
                                        if (tsProbe.networkNodeId === newAssignedProbe.networkNodeId && tsProbe.permissions) {
                                            $scope.probePermissions = tsProbe.permissions;
                                            break;
                                        }
                                    }
                                });
                            }
                            $scope.assignedProbe = newAssignedProbe;
                        }
                        else {
                            $scope.hasAssignedProbe = false;
                            $scope.stopping = false
                        }

                        $scope.allAssignedProbes = $scope.assignedProbesFound ? $scope.assignedProbesFound.tsProbesFound : [];
                        if ($scope.allAssignedProbes) {
                            $scope.allAssignedProbes = $scope.allAssignedProbes
                                .sort((p1: ITsProbeFound, p2: ITsProbeFound) => p1.tsProbe.networkNodeName.localeCompare(p2.tsProbe.networkNodeName));
                        }
                        this.calculateProbeLink($scope);
                    }
                }));
            } else {
                $scope.isLoading = false;
            }
        }

        private buildChanges(selectedProbes: Models.ISelectableProbe[]): Models.ITsChange[] {
            const changes: Models.ITsChange[] = [];
            selectedProbes.forEach((probe: Models.ISelectableProbe) => {
                changes.push({
                    networkNodeId: probe.probe.networkNodeId,
                    defaultProbe: probe.probe.defaultProbe,
                });
            });
            selectedProbes = [];
            return changes;
        }

        private async retrieveProbes($scope: IProbeDirectiveScope): Promise<void> {
            $scope.modalOtherProbes = [];
            $scope.modalSelectedProbes = [];
            const type: Models.NetworkNodeType = await this.networkNodeTypeService.findByName("TNS4200");
            let selectableProbes: Models.ISelectableProbe[] = await this.tsProbeService.getAllWithNodesAndType(type);
            selectableProbes.forEach((selectableProbe: Models.ISelectableProbe) => {
                if (selectableProbe.probe.error === 'unreachable')
                    selectableProbe.node.health.data.healthStatus = 'OFFLINE';
                const probeFound: ITsProbeFound[] = $scope.assignedProbesFound ? $scope.assignedProbesFound.tsProbesFound
                    .filter((blockProbe: ITsProbeFound) => blockProbe.tsProbe.networkNodeId === selectableProbe.probe.networkNodeId) : [];
                if (probeFound.length > 0) {
                    selectableProbe.probe.defaultProbe = probeFound[0].defaultProbe;
                    $scope.modalSelectedProbes.push(selectableProbe);
                } else {
                    $scope.modalOtherProbes.push(selectableProbe);
                }
            })
        }

        private async switchProbing($scope: IProbeDirectiveScope,
                                    changes: Models.ITsChange[],
                                    connectionRoomService: Services.ConnectionRoomService,
                                    tsProbeService: Services.TsProbeService): Promise<void> {
            await tsProbeService.switchProbesByBlockId($scope.blockId, changes).then((result: Models.ProbeOperationResult) => {
                // this.loadVideoFeed($scope, connectionRoomService);
                if (result.result) {
                    tsProbeService.getConnectedProbeByBlockId($scope.blockId).then((response: Models.IFindByTsResults) => {
                        $scope.assignedProbesFound = response;
                        $scope.modalSelectedProbes = [];
                        if (response && response.defaultTsProbe) {
                            $scope.hasAssignedProbe = true;
                            $scope.assignedProbe = response.defaultTsProbe.tsProbe;
                        } else {
                            $scope.hasAssignedProbe = false;
                            $scope.assignedProbe = undefined;
                        }
                    }).catch((err: ngCustom.IReason) => {
                        // We shouldn't change the status of the probing if we get an error
                        // $scope.hasAssignedProbe = false;
                        // $scope.lockingTSProbe = false;
                    });
                } else {
                    this.showProbeErrors(result);
                }
            }).catch((reason: ngCustom.IReason) => {
                const result: Models.ProbeOperationResult =  {
                    result: false,
                    errors: ["Could not connect with TNS"],
                    message: "",
                };
                this.showProbeErrors(result);
            });
        }

        private showProbeErrors(result: Models.ProbeOperationResult): void {
            const errors: string = result.errors && result.errors.length > 0 && result.errors[0] !== "" ?
                result.errors.join("\r\n") : "TNS Generic error";
            this.modalService
                        .showModal({
                            templateUrl: Controllers.ConfirmModalController.templateURL,
                            controller: Controllers.ConfirmModalController.$name,
                            inputs: {
                                title: 'Probe error',
                                description: errors,
                                name: '',
                                type: 'warning',
                            },
                        })
                        .then((modal: ngModal.IModalObject) => {
                            let modalElem: UIkit.ModalElement = UIkit.modal(Controllers.ConfirmModalController.ID,
                                {bgclose: false, modal: false, keyboard: false});
                            modalElem.show();
                            modal.close.then((result: Object) => {
                                modalElem.hide();
                            });
                        });
        }

        private calculateProbeLink($scope: IProbeDirectiveScope): void {
            const defaultTsProbe: ITsProbeFound = $scope.assignedProbesFound ? $scope.assignedProbesFound.defaultTsProbe : undefined;
            if (!$scope.switching && defaultTsProbe !== undefined) {
                const defaultProbeNodeId = defaultTsProbe.tsProbe.networkNodeId;
                if (defaultProbeNodeId !== $scope.assignedProbeLinkId) {
                    this.buildProbeLink($scope, defaultTsProbe);
                }
            } else {
                $scope.probeLink = undefined;
                $scope.assignedProbeLinkId = undefined;
            }
        }

        private buildProbeLink($scope: IProbeDirectiveScope, defaultTsProbe: ITsProbeFound) {
            const defaultProbeNodeId: number = defaultTsProbe.tsProbe.networkNodeId;
            if (this.$rootScope.backendConfig.flexoProxyType === 'PORT_BASED') {
                $scope.probeLink = '#!/ts-probes/' + defaultProbeNodeId + '/' + defaultTsProbe.slot;
            } else {
                this.tsProbeService.getProbeGuiByProbeId(defaultProbeNodeId)
                    .then((probeGui: Models.IDeviceGui) => {
                        this.tsProbeService.getTSProbeGuiSectionUrl(defaultProbeNodeId, 'alarms', defaultTsProbe.slot)
                            .then((uri: string) => {
                                $scope.probeLink = probeGui.guiUrl + uri;
                            });
                    });
            }
            $scope.assignedProbeLinkId = defaultProbeNodeId;
        }
    }
}